// Incoming Email Review (approved HTML:
// Incoming-Email-Consistency-Review-v5.html).
//
// Email and text cannot inherit the other channel's recipient, subject,
// draft, notes or request. Save and Return retains draft, scroll and
// call state. Send never implicitly finishes. Writes gate on
// LIVE_WRITE_GATE_ZOHO_REPLY_TO_CUSTOMER.
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { BoundaryState } from '../_shared/DataBoundary.js';
import { IncomingShell } from './components/IncomingShell.js';
import { INCOMING_EMAIL } from './mockData.js';
import { useScreenRuntime } from '../CurrentActionReview/runtime-clients.js';
import './styles.css';

export interface IncomingEmailScreenProps {
  stateOverride?: BoundaryState;
}

export function IncomingEmailScreen({
  stateOverride,
}: IncomingEmailScreenProps = {}): ReactNode {
  // W2A upper lane: when a signed session is live, probe the Zoho reply
  // gate so the rep sees the real "paused" boundary when it's OFF
  // rather than the illustrative "default" preview. Explicit
  // stateOverride still wins so tests remain deterministic.
  const runtime = useScreenRuntime();
  const { customerId = '' } = useParams();
  const [liveState, setLiveState] = useState<BoundaryState | null>(null);
  useEffect(() => {
    if (!runtime || stateOverride || !customerId) { setLiveState(null); return; }
    let cancelled = false;
    setLiveState({ kind: 'loading', label: 'Loading inbound email…' });
    runtime.work
      .readGate('LIVE_WRITE_GATE_ZOHO_REPLY_TO_CUSTOMER')
      .then((gate) => {
        if (cancelled) return;
        if (!gate.on) {
          setLiveState({
            kind: 'blocked',
            title: 'Incoming email reply is paused',
            reason: gate.reason ?? 'Live-write gate OFF for Zoho reply.',
            gateName: 'LIVE_WRITE_GATE_ZOHO_REPLY_TO_CUSTOMER',
          });
          return;
        }
        setLiveState(null);
      })
      .catch(() => {
        if (cancelled) return;
        // W38 (2026-10-20): /v1/gates/* is bearer-only and answers 401
        // INVALID_BEARER to the url-bootstrap token (MEASURED, W30 audit);
        // 404 means the gate is unmounted. Any failure to read the gate is
        // treated as gate OFF: the shell keeps the real contact body and
        // Send Reply stays visible but inert ("Paused by the live-write
        // gate"). Never an error card over the contact for a probe.
        setLiveState(null);
      });
    return () => { cancelled = true; };
  }, [runtime, stateOverride, customerId]);

  return (
    <IncomingShell
      variant="email"
      mock={INCOMING_EMAIL}
      blockedGate="LIVE_WRITE_GATE_ZOHO_REPLY_TO_CUSTOMER"
      blockedTitle="Incoming email reply is paused"
      blockedReason="Live-write gate OFF for Zoho reply."
      testId="incoming-email-boundary"
      stateOverride={stateOverride ?? liveState ?? undefined}
    />
  );
}

export default IncomingEmailScreen;
